import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Building2, 
  Users, 
  MapPin, 
  Phone, 
  Mail, 
  DollarSign, 
  Calendar,
  FileText,
  Shield,
  Briefcase,
  TrendingUp
} from 'lucide-react';

interface ApplicationContextPanelProps {
  application: {
    id: string;
    company_legal_name?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    w2_employee_count?: number;
    primary_industry?: string;
    naics_code?: string;
    city?: string;
    state?: string;
    estimated_annual_payroll?: string;
    current_provider?: string;
    additional_notes?: string;
    status: string;
  };
}

export const ApplicationContextPanel = ({ application }: ApplicationContextPanelProps) => {
  const companyName = application.company_legal_name || `${application.first_name} ${application.last_name}`;
  
  return (
    <div className="space-y-6">
      <div>
        <h5 className="font-semibold text-lg mb-4">Application Context</h5>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Company Information */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Company Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <div className="text-sm font-medium">{companyName}</div>
                {application.naics_code && (
                  <div className="text-xs text-muted-foreground">NAICS: {application.naics_code}</div>
                )}
              </div>
              
              {application.primary_industry && (
                <div className="flex items-center gap-2 text-sm">
                  <Briefcase className="h-3 w-3 text-muted-foreground" />
                  <span>{application.primary_industry}</span>
                </div>
              )}
              
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-3 w-3 text-muted-foreground" />
                <span>{application.city}, {application.state}</span>
              </div>
              
              {application.status && (
                <Badge variant="outline" className="w-fit">
                  {application.status}
                </Badge>
              )}
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {application.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-3 w-3 text-muted-foreground" />
                  <a href={`mailto:${application.email}`} className="text-blue-600 hover:underline">
                    {application.email}
                  </a>
                </div>
              )}
              
              {application.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-3 w-3 text-muted-foreground" />
                  <a href={`tel:${application.phone}`} className="text-blue-600 hover:underline">
                    {application.phone}
                  </a>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Separator />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Employee Count */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Workforce
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{application.w2_employee_count || 'N/A'}</div>
            <div className="text-xs text-muted-foreground">W-2 Employees</div>
          </CardContent>
        </Card>

        {/* Annual Payroll */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Annual Payroll
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {application.estimated_annual_payroll ? 
                `$${parseFloat(application.estimated_annual_payroll).toLocaleString()}` : 
                'Not provided'
              }
            </div>
            <div className="text-xs text-muted-foreground">Estimated</div>
          </CardContent>
        </Card>

        {/* Current Provider */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Current Provider
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm font-medium">
              {application.current_provider || 'Not specified'}
            </div>
            <div className="text-xs text-muted-foreground">
              {application.current_provider ? 'Existing Provider' : 'No Current Provider'}
            </div>
          </CardContent>
        </Card>
      </div>

      {application.additional_notes && (
        <>
          <Separator />
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Additional Notes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm whitespace-pre-wrap">{application.additional_notes}</div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Quick Action Items */}
      <Card className="border-dashed border-2 border-primary/20 bg-primary/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2 text-primary">
            <TrendingUp className="h-4 w-4" />
            Quote Considerations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="text-sm space-y-1 text-muted-foreground">
            <li>• Company size: {application.w2_employee_count} employees requires {application.w2_employee_count && application.w2_employee_count > 50 ? 'enterprise-level' : 'standard'} PEO services</li>
            <li>• Industry focus: {application.primary_industry} may require specialized compliance</li>
            {application.current_provider && <li>• Competitive switch from {application.current_provider}</li>}
            {application.estimated_annual_payroll && (
              <li>• Target payroll: ${parseFloat(application.estimated_annual_payroll).toLocaleString()} annually</li>
            )}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};